import { App, DirectiveBinding } from 'vue';

interface SanitizeHtmlOptions {
  allowedTags?: string[];
}

interface PartSnapshot {
  text: string;
  modifiers: string[];
}

export const DEFAULT_ALLOWED_TAGS = ['b', 'i'];
let globalAllowedTags = DEFAULT_ALLOWED_TAGS;

// This function will go through a string and take "snapshots" of the different sections and HTML tag modifiers of the string as it iterates.
// It will then return an array of these snapshots with the text of it within.
// For example, if the string is "<b>hello</b> <b><i>world</i></b>", the function will return:
//    [ { text: 'hello', modifiers: ['b'] }, { text: ' ', modifiers: [] }, { text: 'world', modifiers: ['i', 'b'] } ]
function getSanitizedParts(string: string) {
  const LONGEST_TAG_LENGTH = Math.max(...globalAllowedTags.map(tag => tag.length));
  const ALLOWED_TAG_SET = new Set(globalAllowedTags);
  const SIZE_OF_ENDING_BRACKET_CHARACTER = 1;

  const partSnapshots: PartSnapshot[] = [{ text: '', modifiers: [] }];

  const runningModifierTags = new Set<string>();
  for (let generalSearchIndex = 0; generalSearchIndex < string.length; generalSearchIndex++) {
    const char = string[generalSearchIndex];
    const nextChar = string[generalSearchIndex + 1];
    const isPotentialOpenTag = char === '<' && nextChar !== '/';
    const isPotentialCloseTag = char === '<' && nextChar === '/';

    if (!isPotentialOpenTag && !isPotentialCloseTag) {
      partSnapshots[partSnapshots.length - 1].text += char;
      continue;
    }
    const extraSliceIndexingAdjustment = isPotentialOpenTag ? 1 : 2; // for open and closing tags respectively to account for '>' and '/' characters
    const startOffsetIndex = generalSearchIndex + extraSliceIndexingAdjustment;

    for (let extendedTagSearchIndex = startOffsetIndex; extendedTagSearchIndex < startOffsetIndex + LONGEST_TAG_LENGTH + SIZE_OF_ENDING_BRACKET_CHARACTER; extendedTagSearchIndex++) {
      if (string[extendedTagSearchIndex] === '>') {
        const innerTag = string.slice(startOffsetIndex, extendedTagSearchIndex);
        if (!ALLOWED_TAG_SET.has(innerTag)) {
          partSnapshots[partSnapshots.length - 1].text += string.slice(generalSearchIndex, extendedTagSearchIndex + SIZE_OF_ENDING_BRACKET_CHARACTER);
          generalSearchIndex = extendedTagSearchIndex;
          break;
        }
        if (isPotentialCloseTag) {
          runningModifierTags.delete(innerTag);
          partSnapshots.push({
            text: '',
            modifiers: Array.from(runningModifierTags)
          });
        } else {
          runningModifierTags.add(innerTag);
          const lastSnapshot = partSnapshots[partSnapshots.length - 1];
          if (lastSnapshot.text) {
            partSnapshots.push({
              text: '',
              modifiers: Array.from(runningModifierTags)
            });
          } else {
            lastSnapshot.modifiers = Array.from(runningModifierTags);
          }
        }
        generalSearchIndex = extendedTagSearchIndex; // skip past the point everything that was just added
        break;
      } else if (extendedTagSearchIndex === startOffsetIndex + LONGEST_TAG_LENGTH) { // has gotten to the end of potential tag without finding closing '>'. Just add text back to string
        partSnapshots[partSnapshots.length - 1].text += string.slice(generalSearchIndex, generalSearchIndex + 1 + LONGEST_TAG_LENGTH);
        generalSearchIndex = extendedTagSearchIndex - extraSliceIndexingAdjustment;
      }
    }
  }
  return partSnapshots.filter(snapshot => snapshot.text);
}

function setSanitizedInnerHtml(el: HTMLElement, text: string) {
  el.innerHTML = '';
  const allowedParts = getSanitizedParts(text);

  for (const part of allowedParts) {
    if (!part.modifiers.length) {
      el.appendChild(document.createTextNode(part.text));
      continue;
    }

    const createdElements = part.modifiers.map(modifier => document.createElement(modifier));
    if (!createdElements.length) {
      continue;
    }
    createdElements[createdElements.length - 1].innerText = part.text;
    createdElements.reduce((prev, curr) => {
      prev.appendChild(curr);
      return curr;
    });

    const topMostElement = createdElements[0];
    el.appendChild(topMostElement);
  }
}

export function stripHtmlFromString(text: string) {
  const parts = getSanitizedParts(text);
  return parts.map(part => part.text).join('');
}

export default {
  install: (app: App<Element>, options: SanitizeHtmlOptions = {}) => {
    if (options.allowedTags) {
      globalAllowedTags = options.allowedTags;
    }

    app.directive('sanitize-html', {
      mounted: (el: HTMLElement, binding: DirectiveBinding<string>) => {
        setSanitizedInnerHtml(el, binding.value);
      },
      updated: (el: HTMLElement, binding: DirectiveBinding<string>) => {
        if (binding.value === binding.oldValue) {
          return;
        }

        setSanitizedInnerHtml(el, binding.value);
      }
    });
  }
}
